/**
 * usePageLoading Hook
 * Drives the global loading overlay from a page's own data-loading flag.
 *
 * The overlay stays up until `isLoading` flips false — there is no fixed
 * timer, so it never dismisses while a table, chart, or dropdown is still
 * fetching. Reuses whatever loading flag the page already has.
 *
 * Usage:
 *   usePageLoading(loading);                        // Analytics
 *   usePageLoading(isLoading && !hasLoaded);        // list pages with hasLoaded
 *   usePageLoading(isLoading, { message: 'Loading users…' });
 */

import { useEffect, useId } from 'react';
import { useGlobalLoadingContext, LoadingOptions } from '@/contexts/GlobalLoadingContext';
import { useEngineStatus } from '@/hooks/data/useSystemHealth';

export function usePageLoading(isLoading: boolean, options?: LoadingOptions) {
  const { registerLoading, unregisterLoading } = useGlobalLoadingContext();
  const key = useId();

  useEffect(() => {
    if (isLoading) {
      registerLoading(key, options);
    } else {
      unregisterLoading(key);
    }
    // Always clear on unmount so a page navigated-away-from can't pin the overlay.
    return () => unregisterLoading(key);
    // `options` is intentionally excluded: passing an inline object every render
    // would re-register on every render. Message is set once when loading begins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, key, registerLoading, unregisterLoading]);
}

/**
 * The same overlay for data read THROUGH THE AI ENGINE, whose GPU pod may be stopped. Waits for this
 * visit's engine check (bounded by its timeout), then — only if the engine answered — for `isLoading`.
 * With the pod stopped the overlay clears after the check, so the screen shows its "engine not
 * running" state instead of waiting on request timeouts. Returns the engine state for that message.
 */
export function useEngineDataLoading(isLoading: boolean, options?: LoadingOptions) {
  const engine = useEngineStatus();
  usePageLoading(engine.checking || (engine.up && isLoading), options);
  return engine;
}
